<template>
  <div>
    <br />

    <vue-action-buttons :buttons="actionButtons" />

    <div class="card mb-3">
      <div class="card-header">
        <h5 class="mb-0">Détails du dossier d'importation</h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-3">
            <strong>Référence:</strong> {{ consignment.reference }}
          </div>
          <div class="col-md-3">
            <strong>Numéro:</strong> {{ consignment.number }}
          </div>
          <div class="col-md-3">
            <strong>Date:</strong> {{ formatDate(consignment.date) }}
          </div>
          <div class="col-md-3">
            <strong>Société:</strong> {{ consignment.societyFullName }}
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-md-3" v-if="consignment.supplierFullName">
            <strong>Fournisseur (dossier):</strong>
            {{ consignment.supplierFullName }}
          </div>
          <div class="col-md-3">
            <strong>Taux de change:</strong> {{ consignment.exchange_rate }}
          </div>
          <div class="col-md-3">
            <strong>Marge bénéficiaire:</strong>
            {{ parseFloat(consignment.profit_margin || 7.0).toFixed(2) }}%
          </div>
          <div class="col-md-3">
            <strong>Créé par:</strong> {{ consignment.userFullName }}
          </div>
          <div class="col-md-3" v-if="consignment.remark">
            <strong>Remarque:</strong> {{ consignment.remark }}
          </div>
        </div>
      </div>
    </div>

    <br />

    <div
      class="card mb-3"
      v-if="
        consignment.consignmentCosts && consignment.consignmentCosts.length > 0
      "
    >
      <div class="card-header">
        <h5 class="mb-0">Factures du dossier d'importation</h5>
      </div>
      <div class="card-body">
        <vue-data-table
          :headers="headers"
          :data="consignment.consignmentCosts"
          :items-per-page="10"
          :buttons="buttons"
          :summable-columns="summableColumns"
          :show-filter="false"
        >
          <template #cell-totalPriceTTC="{ row }">
            <span
              class="px-2 py-1 rounded"
              :class="
                row.totalPriceTTC > 0
                  ? 'bg-light-primary text-primary'
                  : 'bg-light-danger text-danger'
              "
            >
              {{ row.totalPriceTTC }}
            </span>
          </template>

          <template #cell-totalPriceHT="{ row }">
            <span
              class="px-2 py-1 rounded"
              :class="
                row.totalPriceHT > 0
                  ? 'bg-light-primary text-primary'
                  : 'bg-light-danger text-danger'
              "
            >
              {{ row.totalPriceHT }}
            </span>
          </template>

          <template #cell-totalPriceTVA="{ row }">
            <span
              class="px-2 py-1 rounded"
              :class="
                row.totalPriceTVA > 0
                  ? 'bg-light-primary text-primary'
                  : 'bg-light-danger text-danger'
              "
            >
              {{ row.totalPriceTVA }}
            </span>
          </template>

          <template #cell-cost_reference="{ row }">
            {{ row.costFullName || row.cost_reference }}
          </template>

          <template #cell-invoice_date="{ row }">
            {{ row.invoice_date ? formatDate(row.invoice_date) : "-" }}
          </template>

          <template #cell-invoice_file="{ row }">
            <div
              v-if="row.invoice_file && row.reference"
              class="d-flex flex-wrap gap-1 align-items-center"
            >
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                title="Afficher la facture"
                @click="showInvoiceFile(row)"
              >
                <i class="bi bi-eye"></i> Voir
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                title="Imprimer la facture"
                @click="printInvoiceFile(row)"
              >
                <i class="bi bi-printer"></i> Imprimer
              </button>
            </div>
            <span v-else class="text-muted">—</span>
          </template>

          <template #footer-totalPriceTTC="{ column, sum }">
            <span class="fw-bold text-success">
              {{ sum }}
            </span>
          </template>
        </vue-data-table>
      </div>
    </div>

    <div
      class="card mb-3"
      v-if="
        consignment.consignmentItems && consignment.consignmentItems.length > 0
      "
    >
      <div class="card-header">
        <h5 class="mb-0">Articles du dossier d'importation</h5>
      </div>
      <div class="card-body">
        <vue-data-table
          :headers="itemHeaders"
          :data="consignment.consignmentItems"
          :items-per-page="10"
          :buttons="itemButtons"
          :summable-columns="itemSummableColumns"
          :show-filter="false"
        >
          <template #cell-item_reference="{ row }">
            {{
              row.itemFullName ||
              (row.item && row.item.fullName) ||
              row.item_reference
            }}
          </template>

          <template #cell-amount_mad="{ row }">
            <span
              class="px-2 py-1 rounded"
              :class="
                getAmountMAD(row) > 0
                  ? 'bg-light-primary text-primary'
                  : 'bg-light-danger text-danger'
              "
            >
              {{ getAmountMAD(row).toFixed(2) }}
            </span>
          </template>

          <template #cell-priceHT="{ row }">
            <span
              class="px-2 py-1 rounded"
              :class="
                row.priceHT > 0
                  ? 'bg-light-primary text-primary'
                  : 'bg-light-danger text-danger'
              "
            >
              {{ row.priceHT }}
              <small v-if="row.item && row.item.measuring" class="opacity-75">/ {{ row.item.measuring }}</small>
            </span>
          </template>

          <template #cell-priceTTC="{ row }">
            <span
              class="px-2 py-1 rounded"
              :class="
                row.priceTTC > 0
                  ? 'bg-light-primary text-primary'
                  : 'bg-light-danger text-danger'
              "
            >
              {{ row.priceTTC }}
              <small v-if="row.item && row.item.measuring" class="opacity-75">/ {{ row.item.measuring }}</small>
            </span>
          </template>

          <template #cell-totalPriceHT="{ row }">
            <span
              class="px-2 py-1 rounded"
              :class="
                row.totalPriceHT > 0
                  ? 'bg-light-primary text-primary'
                  : 'bg-light-danger text-danger'
              "
            >
              {{ row.totalPriceHT }}
            </span>
          </template>

          <template #cell-totalPriceTTC="{ row }">
            <span
              class="px-2 py-1 rounded"
              :class="
                row.totalPriceTTC > 0
                  ? 'bg-light-primary text-primary'
                  : 'bg-light-danger text-danger'
              "
            >
              {{ row.totalPriceTTC }}
            </span>
          </template>

          <template #footer-totalPriceHT="{ column, sum }">
            <span class="fw-bold text-success">
              {{ sum }}
            </span>
          </template>

          <template #footer-totalPriceTTC="{ column, sum }">
            <span class="fw-bold text-success">
              {{ sum }}
            </span>
          </template>

          <template #footer-quantity="{ column, sum }">
            <span class="fw-bold text-success">
              {{ sum }}
            </span>
          </template>

          <template #cell-profit_margin="{ row }">
            <span class="px-2 py-1 rounded bg-light-info text-info fw-bold">
              {{ parseFloat(row.profit_margin || 7.0).toFixed(2) }}%
            </span>
          </template>

          <template #cell-priceArticle="{ row }">
            <span class="px-2 py-1 rounded bg-light-warning text-warning fw-bold">
              {{ getPriceArticle(row).toFixed(2) }} MAD
              <small v-if="row.item && row.item.measuring" class="opacity-75">/ {{ row.item.measuring }}</small>
            </span>
          </template>

          <template #cell-sellingPrice="{ row }">
            <span class="px-2 py-1 rounded bg-light-success text-success fw-bold">
              {{ getSellingPrice(row).toFixed(2) }} MAD
              <small v-if="row.item && row.item.measuring" class="opacity-75">/ {{ row.item.measuring }}</small>
            </span>
          </template>

          <template #cell-priceArticlePerPcs="{ row }">
            <span
              v-if="row.item && parseFloat(row.item.pcs) > 0"
              class="px-2 py-1 rounded bg-light-warning text-warning fw-bold"
            >
              {{ getPriceArticlePerPcs(row).toFixed(4) }} MAD
            </span>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell-sellingPricePerPcs="{ row }">
            <span
              v-if="row.item && parseFloat(row.item.pcs) > 0"
              class="px-2 py-1 rounded bg-light-success text-success fw-bold"
            >
              {{ getSellingPricePerPcs(row).toFixed(4) }} MAD
            </span>
            <span v-else class="text-muted">—</span>
          </template>
        </vue-data-table>
      </div>
    </div>

    <!-- Calculation Summary Card -->
    <div
      class="card mb-3"
      v-if="
        (consignment.consignmentCosts &&
          consignment.consignmentCosts.length > 0) ||
        (consignment.consignmentItems &&
          consignment.consignmentItems.length > 0)
      "
    >
      <div class="card-header bg-info text-white">
        <h5 class="mb-0">Calcul de la facture par article</h5>
      </div>
      <div class="card-body">
        <!-- Individual Costs Display -->
        <div
          v-if="
            consignment.consignmentCosts &&
            consignment.consignmentCosts.length > 0
          "
          class="mb-4"
        >
          <h6 class="mb-3">4. Détails des factures :</h6>
          <div
            v-for="(cost, index) in consignment.consignmentCosts"
            :key="index"
            class="row mb-2 pb-2 border-bottom"
          >
            <div class="col-md-4">
              <strong>{{ cost.costFullName || cost.cost_reference }}:</strong>
            </div>
            <div class="col-md-4">
              <span class="text-primary"
                >Total HT:
                {{ parseFloat(cost.totalPriceHT || 0).toFixed(2) }} MAD</span
              >
            </div>
            <div class="col-md-4">
              <span class="text-success"
                >Total TTC:
                {{ parseFloat(cost.totalPriceTTC || 0).toFixed(2) }} MAD</span
              >
            </div>
          </div>
          <div class="row mt-3 pt-2 border-top">
            <div class="col-md-4">
              <strong>Total de toutes les factures :</strong>
            </div>
            <div class="col-md-4">
              <span class="text-primary fw-bold"
                >Total HT: {{ totalAllCostsHT.toFixed(2) }} MAD</span
              >
            </div>
            <div class="col-md-4">
              <span class="text-success fw-bold"
                >Total TTC: {{ totalAllCostsTTC.toFixed(2) }} MAD</span
              >
            </div>
          </div>
        </div>

        <!-- Calculation Steps -->
        <div class="border-top pt-3 mt-3">
          <h6 class="mb-3">Étapes de calcul:</h6>
          <div class="row">
            <div class="col-md-6">
              <strong>5. Somme des factures avec TVA 20 % (TTC) :</strong>
              <span class="ms-2 text-primary fw-bold">
                {{ costsWithTva20Sum.toFixed(2) }} MAD
              </span>
            </div>
            <div class="col-md-6">
              <strong>6. Converti en HT:</strong>
              <span class="ms-2 text-primary fw-bold">
                {{ costsWithTva20HT.toFixed(2) }} MAD
              </span>
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-md-6">
              <strong>Somme des factures sans TVA (HT) :</strong>
              <span class="ms-2 text-primary fw-bold">
                {{ costsWithoutTvaSum.toFixed(2) }} MAD
              </span>
            </div>
            <div class="col-md-6">
              <strong>Total HT des articles:</strong>
              <span class="ms-2 text-primary fw-bold">
                {{ totalItemsHT.toFixed(2) }} MAD
              </span>
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-md-6">
              <strong>7. Total des factures HT (factures + articles) :</strong>
              <span class="ms-2 text-success fw-bold fs-5">
                {{ totalCostsHT.toFixed(2) }} MAD
              </span>
            </div>
            <div class="col-md-6"></div>
          </div>
          <div class="row mt-2">
            <div class="col-md-6">
              <strong>Total quantité des articles:</strong>
              <span class="ms-2 text-primary fw-bold">
                {{ totalItemsQuantity.toFixed(2) }}
              </span>
            </div>
            <div class="col-md-6">
              <strong>PRIX ARTICLE (Total HT / Total Qty):</strong>
              <span class="ms-2 text-success fw-bold fs-5">
                {{ costPerItem.toFixed(2) }} MAD
              </span>
            </div>
          </div>
          <!-- New calculation: Cost per item from costs only (if multiple items) -->
          <div
            class="row mt-3 pt-3 border-top"
            v-if="consignmentItemsCount > 1"
          >
            <div class="col-md-6">
              <strong>Total des factures HT (sans articles) :</strong>
              <span class="ms-2 text-primary fw-bold">
                {{ totalCostsOnlyHT.toFixed(2) }} MAD
              </span>
            </div>
            <div class="col-md-6">
              <strong>Nombre d'articles:</strong>
              <span class="ms-2 text-primary fw-bold">
                {{ consignmentItemsCount }}
              </span>
            </div>
          </div>
          <div class="row mt-2" v-if="consignmentItemsCount > 1">
            <div class="col-md-6">
              <strong
                >Facture par article (Total factures / Nombre d'articles) :</strong
              >
              <span class="ms-2 text-warning fw-bold">
                {{ costPerItemFromCosts.toFixed(2) }} MAD
              </span>
            </div>
            <div class="col-md-6"></div>
          </div>
          <!-- Calculation for each item -->
          <div
            class="row mt-3 pt-3 border-top"
            v-if="consignmentItemsCount > 1"
          >
            <div class="col-md-12">
              <h6 class="mb-3">Calcul du prix article pour chaque item:</h6>
              <div
                v-for="(item, index) in consignment.consignmentItems"
                :key="index"
                class="row mb-2 pb-2 border-bottom"
              >
                <div class="col-md-4">
                  <strong
                    >{{ item.itemFullName || item.item_reference }}:</strong
                  >
                </div>
                <div class="col-md-2">
                  <span class="text-primary">Montant facture alloué :</span>
                  <span class="ms-1 fw-bold"
                    >{{ costPerItemFromCosts.toFixed(2) }} MAD</span
                  >
                </div>
                <div class="col-md-2">
                  <span class="text-primary">Total HT item:</span>
                  <span class="ms-1 fw-bold"
                    >{{
                      parseFloat(item.totalPriceHT || 0).toFixed(2)
                    }}
                    MAD</span
                  >
                </div>
                <div class="col-md-2">
                  <span class="text-success">Total HT:</span>
                  <span class="ms-1 fw-bold"
                    >{{
                      (
                        costPerItemFromCosts +
                        parseFloat(item.totalPriceHT || 0)
                      ).toFixed(2)
                    }}
                    MAD</span
                  >
                </div>
                <div class="col-md-2">
                  <span class="text-warning fw-bold">
                    Prix achat / {{ (item.item && item.item.measuring) || 'unité' }}:
                  </span>
                  <span class="ms-1 fw-bold fs-5">
                    {{ getPriceArticle(item).toFixed(2) }} MAD
                  </span>
                  <div
                    v-if="item.item && parseFloat(item.item.pcs) > 0"
                    class="mt-1 text-secondary"
                    style="font-size:0.85em"
                  >
                    <span class="fw-semibold">Prix achat / PCS:</span>
                    {{ getPriceArticlePerPcs(item).toFixed(4) }} MAD
                    <br />
                    <small class="opacity-75">
                      1 {{ item.item.measuring }} = {{ item.item.pcs }} PCS
                    </small>
                  </div>
                  <div
                    v-if="item.item && parseFloat(item.item.pcs) > 0"
                    class="mt-1 text-success"
                    style="font-size:0.85em"
                  >
                    <span class="fw-semibold">Prix vente / {{ item.item.measuring }}:</span>
                    {{ getSellingPrice(item).toFixed(2) }} MAD
                    <br />
                    <span class="fw-semibold">Prix vente / PCS:</span>
                    {{ getSellingPricePerPcs(item).toFixed(4) }} MAD
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <br />
    <br />
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import moment from "moment";
import { printData } from "../../utils/printUtils";
import constant from "../../constant";

export default {
  data() {
    return {
      headers: [
        { field: "cost_reference", label: "Facture", class: "" },
        { field: "invoice_number", label: "N° Facture", class: "" },
        { field: "invoice_date", label: "Date Facture", class: "" },
        { field: "supplierFullName", label: "Fournisseur", class: "" },
        { field: "invoice_file", label: "Fichier joint", class: "" },
        {
          field: "totalPriceTTC",
          label: "Total TTC",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "totalPriceHT",
          label: "Total HT",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "totalPriceTVA",
          label: "Total TVA",
          class: "fw-bold",
          isAmount: true,
        },
        { field: "tva", label: "TVA %", class: "" },
        { field: "paymentMethod", label: "Méthode de paiement", class: "" },
        { field: "paymentDate", label: "Date de paiement", class: "" },
        { field: "userFullName", label: "Créer par", class: "" },
      ],
      // Button actions
      buttons: [],

      // Columns to summarize
      summableColumns: [
        { field: "totalPriceTTC", class: "fw-bold" },
        { field: "totalPriceHT", class: "fw-bold" },
        { field: "totalPriceTVA", class: "fw-bold" },
      ],

      // Item headers
      itemHeaders: [
        { field: "item_reference", label: "Article", class: "" },
        { field: "currency", label: "Devise", class: "" },
        {
          field: "exchange_rate",
          label: "Taux de change",
          class: "",
          isAmount: true,
        },
        {
          field: "amount_currency",
          label: "Montant (Devise)",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "amount_mad",
          label: "Montant (MAD)",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "priceHT",
          label: "Prix HT",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "priceTTC",
          label: "Prix TTC",
          class: "fw-bold",
          isAmount: true,
        },
        { field: "quantity", label: "Quantité", class: "fw-bold" },
        {
          field: "totalPriceHT",
          label: "Total HT",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "totalPriceTTC",
          label: "Total TTC",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "profit_margin",
          label: "Marge %",
          class: "fw-bold",
          isAmount: true,
        },
        {
          field: "priceArticle",
          label: "Prix achat / unité",
          class: "fw-bold text-warning",
          isAmount: true,
        },
        {
          field: "priceArticlePerPcs",
          label: "Prix achat / PCS",
          class: "fw-bold text-warning",
          isAmount: true,
        },
        {
          field: "sellingPrice",
          label: "Prix vente / unité",
          class: "fw-bold text-success",
          isAmount: true,
        },
        {
          field: "sellingPricePerPcs",
          label: "Prix vente / PCS",
          class: "fw-bold text-success",
          isAmount: true,
        },
        { field: "remark", label: "Remarque", class: "" },
        { field: "userFullName", label: "Créé par", class: "" },
      ],
      // Item button actions
      itemButtons: [],

      // Item columns to summarize
      itemSummableColumns: [
        { field: "totalPriceHT", class: "fw-bold" },
        { field: "totalPriceTTC", class: "fw-bold" },
        { field: "quantity", class: "fw-bold" },
      ],

      exportExcelLoading: false,
      reference: this.$route.params.reference,
      transferLoading: false, // kept for template compatibility (global $loading handles spinner)
    };
  },

  async mounted() {
    await this.$store.dispatch(
      "consignment/show",
      this.$route.params.reference
    );
  },
  computed: {
    ...mapGetters("consignment", {
      consignment: "getConsignment",
    }),
    actionButtons() {
      return [
        {
          label: "Imprimer",
          class: "btn btn-outline-info",
          icon: "bi bi-printer",
          action: () => this.printConsignment(this.reference),
        },
        {
          label: this.exportExcelLoading ? "Export…" : "Export Excel",
          class: "btn btn-success",
          icon: "bi bi-file-earmark-excel-fill",
          action: () => this.exportDetailsToExcel(),
          loading: () => this.exportExcelLoading,
          disabled: () => this.exportExcelLoading,
        },
        {
          label:
            this.consignment && this.consignment.purchase_reference
              ? `Achat ${this.consignment.purchase_reference}`
              : "Vers achat",
          class: "btn btn-outline-success",
          icon: "bi bi-bag-plus",
          action: () => this.confirmQueueConsignmentToPurchase(),
          condition: () =>
            !this.consignment || !this.consignment.purchase_reference,
        },
        {
          label: "Modifier",
          class: "btn btn-outline-primary",
          icon: "bi bi-pencil-square",
          action: () => this.editConsignment(this.reference),
        },
        {
          label: "Supprimer",
          class: "btn btn-outline-danger",
          icon: "bi bi-trash",
          action: () => this.remove(this.reference),
          condition: () =>
            this.$userRole === "Admin" || this.$userRole === "Commercial",
        },
        {
          label: "Actualiser",
          class: "btn btn-outline-dark",
          icon: "bi bi-arrow-clockwise",
          action: this.refresh,
        },
      ];
    },
    // Sum of costs with TVA = 20% (TTC)
    costsWithTva20Sum() {
      if (!this.consignment || !this.consignment.consignmentCosts) return 0;
      return this.consignment.consignmentCosts
        .filter((cost) => {
          const tva = parseFloat(cost.tva);
          return !isNaN(tva) && tva === 20;
        })
        .reduce((sum, cost) => sum + parseFloat(cost.totalPriceTTC || 0), 0);
    },
    // Convert costs with TVA 20% from TTC to HT
    costsWithTva20HT() {
      if (this.costsWithTva20Sum === 0) return 0;
      return this.costsWithTva20Sum / 1.2;
    },
    // Sum of costs without TVA (TVA = 0)
    costsWithoutTvaSum() {
      if (!this.consignment || !this.consignment.consignmentCosts) return 0;
      return this.consignment.consignmentCosts
        .filter((cost) => {
          const tva = parseFloat(cost.tva || 0);
          return isNaN(tva) || tva === 0;
        })
        .reduce((sum, cost) => sum + parseFloat(cost.totalPriceHT || 0), 0);
    },
    // Total items HT
    totalItemsHT() {
      if (!this.consignment || !this.consignment.consignmentItems) return 0;
      return this.consignment.consignmentItems.reduce(
        (sum, item) => sum + parseFloat(item.totalPriceHT || 0),
        0
      );
    },
    // Total costs HT = (costs with TVA 20% converted to HT) + (costs without TVA) + (items totalPriceHT)
    totalCostsHT() {
      return (
        this.costsWithTva20HT + this.costsWithoutTvaSum + this.totalItemsHT
      );
    },
    // Total quantity of all items
    totalItemsQuantity() {
      if (!this.consignment || !this.consignment.consignmentItems) return 0;
      return this.consignment.consignmentItems.reduce(
        (sum, item) => sum + parseFloat(item.quantity || 0),
        0
      );
    },
    // Cost per item = Total costs HT / Total quantity
    costPerItem() {
      if (this.totalItemsQuantity === 0) return 0;
      return this.totalCostsHT / this.totalItemsQuantity;
    },
    // Total of all costs HT
    totalAllCostsHT() {
      if (!this.consignment || !this.consignment.consignmentCosts) return 0;
      return this.consignment.consignmentCosts.reduce(
        (sum, cost) => sum + parseFloat(cost.totalPriceHT || 0),
        0
      );
    },
    // Total of all costs TTC
    totalAllCostsTTC() {
      if (!this.consignment || !this.consignment.consignmentCosts) return 0;
      return this.consignment.consignmentCosts.reduce(
        (sum, cost) => sum + parseFloat(cost.totalPriceTTC || 0),
        0
      );
    },
    // Count of consignment items
    consignmentItemsCount() {
      if (!this.consignment || !this.consignment.consignmentItems) return 0;
      return this.consignment.consignmentItems.length;
    },
    // Total costs HT (without items) - sum of all consignment_costs only
    totalCostsOnlyHT() {
      if (!this.consignment || !this.consignment.consignmentCosts) return 0;
      return this.consignment.consignmentCosts.reduce(
        (sum, cost) => sum + parseFloat(cost.totalPriceHT || 0),
        0
      );
    },
    // Cost per item from costs only (if multiple items)
    costPerItemFromCosts() {
      if (this.consignmentItemsCount <= 1) return 0;
      if (this.consignmentItemsCount === 0) return 0;
      return this.totalCostsOnlyHT / this.consignmentItemsCount;
    },
  },
  methods: {
    async refresh() {
      await this.$store.dispatch(
        "consignment/show",
        this.$route.params.reference
      );
    },

    async remove(reference) {
      await this.$store.dispatch("consignment/destroy", reference);
    },

    editConsignment(ref) {
      this.$router.push({
        name: "consignments-edit",
        params: { reference: ref },
      });
    },

    async printConsignment(reference) {
      await printData(
        [],
        `${constant.api}consignments_print_page`,
        "GET",
        reference,
        this
      );
    },

    async exportDetailsToExcel() {
      if (!this.reference || this.exportExcelLoading) return;
      this.exportExcelLoading = true;
      try {
        await this.$store.dispatch(
          "consignment/exportDetailsExcel",
          this.reference
        );
      } finally {
        this.exportExcelLoading = false;
      }
    },

    async confirmQueueConsignmentToPurchase() {
      if (this.consignment && this.consignment.purchase_reference) return;
      const confirmed = await this.$confirm({
        title: "Créer l'achat depuis ce dossier ?",
        message:
          "Un bon d'achat sera enregistré (fournisseur = celui renseigné sur les factures du dossier, un seul fournisseur autorisé), date du dossier, totaux comme sur cet écran, stock au nom de la société. Cette action ne peut être effectuée qu'une seule fois. Continuer ?",
        confirmButtonText: "Oui",
        cancelButtonText: "Non",
      });
      if (!confirmed) return;
      const result = await this.$store.dispatch(
        "consignment/queuePurchaseItems",
        this.reference
      );
      if (result) {
        await this.$store.dispatch("purchase/getAll");
        await this.$store.dispatch("purchase/getAllItems");
        await this.$store.dispatch("consignment/show", this.reference);
      }
    },

    formatDate(value) {
      if (!value) return null;
      return moment(value).format("DD-MM-YYYY");
    },
    // Calculate amount in MAD from amount_currency and exchange_rate
    getAmountMAD(item) {
      if (!item.amount_currency) return 0;
      const amountCurrency = parseFloat(item.amount_currency) || 0;
      const exchangeRate = parseFloat(item.exchange_rate) || 1;
      const currency = item.currency || "MAD";

      if (currency === "MAD") {
        return amountCurrency;
      }
      return amountCurrency * exchangeRate;
    },
    // Calculate price per article for each item
    // Formula: (costPerItemFromCosts + item.totalPriceHT) / item.quantity
    getPriceArticle(item) {
      if (this.consignmentItemsCount <= 1) {
        // If only one item, return the regular priceHT
        return parseFloat(item.priceHT || 0);
      }

      const costPerItem = this.costPerItemFromCosts;
      const itemTotalHT = parseFloat(item.totalPriceHT || 0);
      const quantity = parseFloat(item.quantity || 1);

      if (quantity === 0) return 0;

      // Total HT = cost per item (from costs) + item's totalPriceHT
      const totalHT = costPerItem + itemTotalHT;

      // Price article = Total HT / quantity
      return totalHT / quantity;
    },
    // Calculate selling price for each item using item's profit margin
    getSellingPrice(item) {
      const priceArticle = this.getPriceArticle(item);
      const profitMargin = parseFloat(item.profit_margin || 7.0);
      return priceArticle * (1 + profitMargin / 100);
    },
    // Price per PCS = price per measuring unit / pcs per unit
    getPriceArticlePerPcs(item) {
      const pcs = item.item ? parseFloat(item.item.pcs) : 0;
      if (!pcs || pcs <= 0) return 0;
      return this.getPriceArticle(item) / pcs;
    },
    getSellingPricePerPcs(item) {
      const pcs = item.item ? parseFloat(item.item.pcs) : 0;
      if (!pcs || pcs <= 0) return 0;
      return this.getSellingPrice(item) / pcs;
    },
    async fetchInvoiceBlob(reference) {
      const apiUrl =
        constant.api + `consignments_costs/${reference}/download-invoice`;
      const response = await this.$api.instance.get(apiUrl, {
        responseType: "blob",
      });
      const ct =
        (response.headers && response.headers["content-type"]) ||
        "application/pdf";
      return new Blob([response.data], { type: ct });
    },

    getInvoicePublicUrl(row) {
      if (!row || !row.invoice_file) return null;
      const path = String(row.invoice_file).replace(/^\/+/, "");
      const base = String(constant.url || "").replace(/\/+$/, "");
      return `${base}/storage/${path}`;
    },

    async showInvoiceFile(row) {
      if (!row || !row.reference || !row.invoice_file) return;
      try {
        const blob = await this.fetchInvoiceBlob(row.reference);
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (e) {
        console.error(e);
        const pub = this.getInvoicePublicUrl(row);
        if (pub) {
          window.open(pub, "_blank", "noopener,noreferrer");
        } else {
          this.$store.dispatch(
            "notification/store",
            {
              status: "error",
              message: "Impossible d'ouvrir le fichier de la facture.",
            },
            { root: true }
          );
        }
      }
    },

    async printInvoiceFile(row) {
      if (!row || !row.reference || !row.invoice_file) return;
      try {
        const blob = await this.fetchInvoiceBlob(row.reference);
        const url = URL.createObjectURL(blob);
        const w = window.open(url, "_blank", "noopener,noreferrer");
        if (w) {
          const tryPrint = () => {
            try {
              w.focus();
              w.print();
            } catch (err) {
              /* ignore */
            }
          };
          w.addEventListener("load", () => setTimeout(tryPrint, 400));
          setTimeout(tryPrint, 1200);
        }
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (e) {
        console.error(e);
        const pub = this.getInvoicePublicUrl(row);
        if (pub) {
          const w = window.open(pub, "_blank", "noopener,noreferrer");
          if (w) {
            w.addEventListener("load", () =>
              setTimeout(() => {
                try {
                  w.print();
                } catch (err) {
                  /* ignore */
                }
              }, 400)
            );
          }
        } else {
          this.$store.dispatch(
            "notification/store",
            {
              status: "error",
              message: "Impossible d'imprimer le fichier de la facture.",
            },
            { root: true }
          );
        }
      }
    },
  },
};
</script>
